import React, { useState, useEffect, useMemo } from 'react';
import Crud_Catalogos from '../herramientas/Crud_Catalogos';
import { 
  Card, Button, Table, Badge, Avatar, 
  Grid, Alert, Container, Dimmer
} from 'tabler-react';
import ModalLayout from './ModalLayout.react';
import ListarAlertasPorCategoriaDeMovimiento from './ListarAlertasPorCategoriaDeMovimiento';

const ListarMovimientosPanelSupervisor = ({
  categoria, 
  idAlertaCategoriaDesvioRuta, 
  idAlertaCategoriaGpsSinSenal, 
  idAlertaCategoriaUnidadDetenida, 
  idIncidencia1, 
  idIncidencia2, 
  idIncidencia3,
  idusuario,
  onActualizarConteos // Nueva prop para notificar al PanelSupervisor
}) => {
  //console.log(categoria);
  const [movimientos, setMovimientos] = useState([]);
  const [filteredMovimientos, setFilteredMovimientos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarMensaje, setMostrarMensaje] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [lastUpdate, setLastUpdate] = useState(0); // Para forzar actualizaciones

  // Obtener usuario de sessionStorage solo si no se proporciona como prop
  const currentUser = useMemo(() => {
    if (idusuario) return { id: idusuario };
    
    try {
      const userFromSession = JSON.parse(sessionStorage.getItem("currentUser"));
      return userFromSession?.[0]?.usuario || { id: null };
    } catch (error) {
      console.error("Error al obtener usuario de sesión:", error);
      return { id: null };
    }
  }, [idusuario]);

  // Determinar el endpoint basado en la categoría
  const getEndpoint = () => {
    if (!currentUser.id) return null;
    
    const baseUrl = "navieraMovimientos/";
    
    const endpoints = {
      "Desvío de ruta": `${baseUrl}listaMovimientosNavieras/${currentUser.id}/${idAlertaCategoriaDesvioRuta}`,
      "GPS excede tiempo en zona de intermitencia": `${baseUrl}listaMovimientosIncidenciaNavieras/${idIncidencia1}/${idIncidencia2}/${currentUser.id}`,
      "Solicitud de operativo": `${baseUrl}listaMovimientosIncidenciaNavieras/${idIncidencia3}/0/${currentUser.id}`,
      "Temporizador": `${baseUrl}listaMovimientosTemporizadoresNavieras/${currentUser.id}`,
      "Servicios con GPS": `${baseUrl}listaMovimientosGpsNavieras/${currentUser.id}`,
      "Servicios sin GPS": `${baseUrl}listaMovimientosSinGpsNavieras/${currentUser.id}`,
      "Unidad programada": `${baseUrl}listaMovimientosUnidadProgramadaConGpsNavieras/${currentUser.id}`,
      "GPS sin señal": `${baseUrl}listaMovimientosNavieras/${currentUser.id}/${idAlertaCategoriaGpsSinSenal}`,
      "Unidad detenida": `${baseUrl}listaMovimientosNavieras/${currentUser.id}/${idAlertaCategoriaUnidadDetenida}`
    };
    
    return endpoints[categoria] || null;
  };

  const cargarMovimientos = async () => {
    setCargando(true);
    setMostrarMensaje(false);
    
    const endpoint = getEndpoint();
    if (!endpoint) {
      setCargando(false);
      setMostrarMensaje(true);
      return;
    }
    
    try {
      const resultado = await crud("get", "", "", endpoint, "listaMovimientos");
      if (Array.isArray(resultado) && resultado.length > 0) {
        setMovimientos(resultado);
        setFilteredMovimientos(resultado);
      } else {
        setMovimientos([]);
        setFilteredMovimientos([]);
        
        // Mostrar mensaje después de un breve retraso
        setTimeout(() => {
          setMostrarMensaje(true);
        }, 500);
      }
    } catch (error) {
      console.error("Error al cargar movimientos:", error);
      setMovimientos([]);
      setFilteredMovimientos([]);
      setMostrarMensaje(true);
    } finally {
      setCargando(false);
    }
  };

   // Función callback que se llamará cuando se atienda una alerta
   const handleAlertaAtendida = (categoriaAlertaAtendida) => {
    //console.log("Alerta atendida en categoría:", categoriaAlertaAtendida);
    
    // Actualizar la lista de movimientos
    setLastUpdate(Date.now());
    
    // Notificar al componente padre (PanelSupervisor) si existe el callback
    if (typeof onActualizarConteos === 'function') {
      //console.log("Notificando a Panel Supervisor sobre alerta atendida");
      onActualizarConteos(categoriaAlertaAtendida);
    }
  };

  // Cargar datos cuando cambia la categoría o después de atender una alerta
  useEffect(() => {
    if (categoria) {
      cargarMovimientos();
    }
  }, [categoria, lastUpdate]);

  // Filtrar movimientos cuando cambia el filtro
  useEffect(() => {
    if (!filtro.trim()) {
      setFilteredMovimientos(movimientos);
      return;
    }
    
    const filtroLower = filtro.toLowerCase();
    const resultados = movimientos.filter(item => 
      (item.id_movimiento && item.id_movimiento.toString().includes(filtroLower)) ||
      (item.plataforma && item.plataforma.toLowerCase().includes(filtroLower)) ||
      (item.nombre_gps && item.nombre_gps.toLowerCase().includes(filtroLower)) ||
      (item.cliente && item.cliente.toLowerCase().includes(filtroLower))
    );
    
    setFilteredMovimientos(resultados);
  }, [filtro, movimientos]);

  const crud = (metodo = "get", obj = [], id = "", catalogo = "", stateVar = "") => {
    return Crud_Catalogos(catalogo, "", metodo, id, obj, "", stateVar, "", [])
      .then(returnVal => {
        if (Array.isArray(returnVal)) {
          return returnVal;
        }
        return [];
      })
      .catch(err => { 
        console.error("Error en operación CRUD:", err);
        return []; 
      });
  };

  // Determinar si mostrar columna de alertas basado en la categoría
  const mostrarColumnasAlertas = ["Desvío de ruta", "GPS sin señal", "Unidad detenida"].includes(categoria);
  // Determinar si mostrar columna de incidencias basado en la categoría
  const mostrarColumnasIncidencias = ["GPS excede tiempo en zona de intermitencia", "Solicitud de operativo"].includes(categoria);
  // Determinar si mostrar columna de temporizador basado en la categoría
  const mostrarColumnasTemporizador = ["Temporizador"].includes(categoria);

  // Obtener color según la categoría para los badges
  const getCategoryColor = () => {
    const colors = {
      "Desvío de ruta": "danger",
      "GPS sin señal": "warning",
      "Unidad detenida": "info",
      "Temporizador": "primary",
      "Servicios con GPS": "success",
      "Servicios sin GPS": "secondary",
      "Unidad programada": "dark"
    };
    
    return colors[categoria] || "primary";
  };

  // Renderizar el estado de carga
  if (cargando) {
    return (
      <Dimmer active>
        <div className="loader"></div>
        <p className="h4 text-center text-muted mt-4">Cargando movimientos...</p>
      </Dimmer>
    );
  }

  return (
    <Container fluid>
      {movimientos.length > 0 ? (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <Badge color={getCategoryColor()} className="mr-2">
                  {filteredMovimientos.length} movimientos
                </Badge>
                <span className="text-muted">
                  con <strong>{categoria}</strong>
                </span>
              </div>
              
              <div className="d-flex">
                <div className="input-icon mr-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                  />
                  <span className="input-icon-addon">
                    <i className="fe fe-search"></i>
                  </span>
                </div>
                
                <Button
                  outline
                  color="primary"
                  icon="refresh-cw"
                  onClick={cargarMovimientos}
                >
                  Actualizar
                </Button>
              </div>
            </div>
          </Card.Header>
          
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table cards={true} striped={true} className="table-vcenter">
                <Table.Header>
                  <Table.Row>
                    <Table.ColHeader>PLATAFORMA</Table.ColHeader>
                    <Table.ColHeader>IDMOV</Table.ColHeader>
                    <Table.ColHeader>GPS</Table.ColHeader>
                    <Table.ColHeader>CLIENTE</Table.ColHeader>
                    {mostrarColumnasAlertas && <Table.ColHeader>ALERTAS</Table.ColHeader>}
                    {mostrarColumnasIncidencias && <Table.ColHeader>FECHA INCIDENCIA</Table.ColHeader>}
                    {mostrarColumnasTemporizador && <Table.ColHeader>FECHA TEMPORIZADOR</Table.ColHeader>}
                  </Table.Row>
                </Table.Header>
                
                <Table.Body>
                  {filteredMovimientos.map((elemento, index) => (
                    <Table.Row key={index}>
                      <Table.Col>
                        <div className="d-flex align-items-center">
                          <Avatar
                            size="sm"
                            className="mr-2"
                            imageURL={null}
                            icon="truck"
                            color={getCategoryColor()}
                          />
                          {elemento.plataforma || "N/A"}
                        </div>
                      </Table.Col>
                      
                      <Table.Col>
                        <Badge color="dark">
                          {elemento.id_movimiento}
                        </Badge>
                      </Table.Col>
                      
                      <Table.Col>
                        <span className="status-icon bg-success mr-1"></span>
                        {elemento.nombre_gps || "Sin GPS"}
                      </Table.Col>
                      
                      <Table.Col>
                        {elemento.cliente || "Sin cliente"}
                      </Table.Col>
                      
                      {mostrarColumnasAlertas && (
                        <Table.Col>
                          <ModalLayout
                            tamanio={'90%'}
                            title={""}
                            name={
                              <Button pill size="sm" outline color="success">
                                <i className="fe fe-bell mr-1"></i>
                                Ver alertas
                              </Button>
                            }
                            formulario={
                              <ListarAlertasPorCategoriaDeMovimiento
                                idmovimiento={elemento.id_movimiento}
                                categoria={categoria}
                                idAlertaCategoriaDesvioRuta={idAlertaCategoriaDesvioRuta}
                                idAlertaCategoriaGpsSinSenal={idAlertaCategoriaGpsSinSenal}
                                idAlertaCategoriaUnidadDetenida={idAlertaCategoriaUnidadDetenida}
                                movimiento={elemento}
                                idusuario={currentUser.id}
                                onAlertaAtendida={handleAlertaAtendida} // Pasar callback a componente hijo
                              />
                            }
                            hiddenModal={""}
                            Fclose={() => {}}
                          />
                        </Table.Col>
                      )}
                      {mostrarColumnasIncidencias && (
                        <Table.Col>
                          {elemento.fecha_incidencia || "N/A"}
                        </Table.Col>
                      )}
                      {mostrarColumnasTemporizador && (
                        <Table.Col>
                          {elemento.fecha_temporizador || "N/A"}
                        </Table.Col>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : (
        mostrarMensaje && (
          <Alert type="primary" icon="info-circle">
            <h4>Sin resultados</h4>
            <p>No hay movimientos con categoría {categoria}.</p>
          </Alert>
        )
      )}
    </Container>
  );
};

export default ListarMovimientosPanelSupervisor;